import React, { useContext, useState, useRef, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import "./CommunityPage.scss";
import Back from "../../../components/back-button/Back";
import Compass from "../../../components/compass/Compass";
import MarkerText from "../../../components/map/markers/MarkerText";
import { PIN_OFFSETS } from "../../../components/map/markers/markerUtils";
import { MainContext } from "../../../contexts/MainContext";
import Modal from "../../../components/property-page-modal-window/Modal";
import ProgressiveImage from "../../../components/progressive-image/ProgressiveImage";
import Loader from "../../../components/loader-spinner/Loader";

const PIN_SIZE = { width: 60, height: 60 };

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const calculatePolygon = (coordsList) => {
    if (!coordsList || !Array.isArray(coordsList) || coordsList.length === 0) {
        // Return default values when coords are invalid or missing
        return {
            polygonSize: { width: 0, height: 0 },
            polygonCenter: [0, 0],
        };
    }

    let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

    coordsList.forEach(([x, y]) => {
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
    });

    const polygonSize = { width: maxX - minX, height: maxY - minY };
    const polygonCenter = [(minX + maxX) / 2, (minY + maxY) / 2];

    return { polygonSize, polygonCenter };
};

export default function CommunityPage() {
    const { cityName, communityName } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const mapRef = useRef();
    const mapBackgroundRef = useRef();
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [transitionType, setTransitionType] = useState("wheel");
    const dragStart = useRef({ x: 0, y: 0 });
    const offsetStart = useRef({ x: 0, y: 0 });
    const [activePolygon, setActivePolygon] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [naturalSize, setNaturalSize] = useState({
        width: 8000,
        height: 4500,
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isVerySmallDevice, setIsVerySmallDevice] = useState(
        window.innerWidth <= 480
    );

    // Initialize loader to be visible
    const [showLoader, setShowLoader] = useState(true);

    // Track if the image has loaded
    const [imageLoaded, setImageLoaded] = useState(false);

    const {
        allCommunities,
        allProperties,
        allGroups,
        facilityMarkers = [],
        setModal,
    } = useContext(MainContext);

    // Move community definition before any references to it
    const community = allCommunities.find(
        (comm) => comm.path === `/${cityName}/${communityName}`
    );

    // Get path parts to check if we're loading a property page
    const pathParts = location.pathname.split("/");
    const isPropertyPage = pathParts.length === 4 && pathParts[3];

    // Always show loader when community is null or still loading
    useEffect(() => {
        if (!community) {
            setShowLoader(true);
        }
    }, [community]);

    // Only hide loader when not on property page and after a brief delay
    useEffect(() => {
        // On a property page, the loader is managed by the property navigation logic
        // Otherwise, we hide it when the image loads
        if (!isPropertyPage && imageLoaded && community) {
            const timer = setTimeout(() => {
                setShowLoader(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isPropertyPage, imageLoaded, community]);

    // Create ref for zoomToPolygon to avoid circular dependency in useEffect
    const zoomToPolygonRef = useRef(null);

    // Track the last time we zoomed to a property
    const lastZoomTimestampRef = useRef(0);

    // Track if we're currently in a zoom transition to prevent multiple calls
    const [isZooming, setIsZooming] = useState(false);

    // New state to track manual interactions
    const [isManualInteraction, setIsManualInteraction] = useState(false);

    const defaultZoom = community?.defaultZoom || 1;
    const minZoom = 1;
    const maxZoom = 3;
    const defaultPosition = community?.defaultPosition || null;

    // Reset isManualInteraction after a delay
    useEffect(() => {
        if (isManualInteraction) {
            const timeout = setTimeout(() => {
                setIsManualInteraction(false);
            }, 2000); // 2-second delay to match original logic
            return () => clearTimeout(timeout);
        }
    }, [isManualInteraction]);

    const clampOffset = (off, currentZoom = zoom) => {
        const container = mapRef.current;
        const imageEl = mapBackgroundRef.current;
        if (!container || !imageEl) return off;

        const { width: containerWidth, height: containerHeight } =
            container.getBoundingClientRect();

        const ratio = naturalSize.width / naturalSize.height;
        const containerRatio = containerWidth / containerHeight;

        let scaledWidth, scaledHeight;
        if (ratio < containerRatio) {
            scaledWidth = containerWidth * currentZoom;
            scaledHeight = ((containerWidth * 1) / ratio) * currentZoom;
        } else {
            scaledWidth = containerHeight * ratio * currentZoom;
            scaledHeight = containerHeight * currentZoom;
        }

        let minX, maxX, minY, maxY;

        if (scaledWidth <= containerWidth) {
            minX = (containerWidth - scaledWidth) / 2;
            maxX = (containerWidth - scaledWidth) / 2;
        } else {
            minX = containerWidth - scaledWidth;
            maxX = 0;
        }

        if (scaledHeight <= containerHeight) {
            minY = (containerHeight - scaledHeight) / 2;
            maxY = (containerHeight - scaledHeight) / 2;
        } else {
            minY = containerHeight - scaledHeight;
            maxY = 0;
        }

        return {
            x: Math.round(clamp(off.x, minX, maxX)),
            y: Math.round(clamp(off.y, minY, maxY)),
        };
    };

    const centerMap = (zoomLevel = zoom) => {
        const container = mapRef.current;
        const imageEl = mapBackgroundRef.current;
        if (!container || !imageEl) return;

        const { width: containerWidth, height: containerHeight } =
            container.getBoundingClientRect();
        const { width: imageWidth, height: imageHeight } =
            imageEl.getBoundingClientRect();

        let newOffset;
        if (defaultPosition) {
            newOffset = {
                x:
                    containerWidth / 2 -
                    (defaultPosition.x / naturalSize.width) *
                        imageWidth *
                        zoomLevel,
                y:
                    containerHeight / 2 -
                    (defaultPosition.y / naturalSize.height) *
                        imageHeight *
                        zoomLevel,
            };
        } else {
            newOffset = {
                x: (containerWidth - imageWidth * zoomLevel) / 2,
                y: (containerHeight - imageHeight * zoomLevel) / 2,
            };
        }

        setOffset(clampOffset(newOffset, zoomLevel));
    };

    const handleImageLoad = () => {
        const imageEl = mapBackgroundRef.current;
        if (imageEl) {
            setNaturalSize({
                width: imageEl.naturalWidth,
                height: imageEl.naturalHeight,
            });
            // Only center the map if we're not on a property page
            if (!isPropertyPage) {
                centerMap();
            }
        }
    };

    // Enhanced image load handler that also sets imageLoaded
    const handleEnhancedImageLoad = () => {
        const imageEl = mapBackgroundRef.current;
        if (imageEl) {
            setNaturalSize({
                width: imageEl.naturalWidth,
                height: imageEl.naturalHeight,
            });
            // Only center the map if we're not on a property page
            if (!isPropertyPage) {
                centerMap();
            }
        }
        setImageLoaded(true);
    };

    // Check URL for property and zoom to it once image is loaded
    useEffect(() => {
        if (
            !imageLoaded ||
            !community ||
            !allProperties.length ||
            !zoomToPolygonRef.current
        ) {
            console.log("Not checking URL yet, waiting for resources:", {
                imageLoaded,
                community: !!community,
                propertiesLoaded: allProperties.length > 0,
                zoomFnReady: !!zoomToPolygonRef.current,
            });
            // Keep loader visible while waiting for resources
            return;
        }

        const currentPath = window.location.pathname;
        const pathParts = currentPath.split("/");
        console.log("Checking URL parts:", pathParts, "Raw path:", currentPath);

        // Skip URL check if a manual interaction occurred recently
        if (isManualInteraction) {
            console.log(
                "Skipping URL check - recent manual interaction detected"
            );
            setShowLoader(false);
            return;
        }

        if (pathParts.length === 4 && pathParts[3]) {
            const propertyName = pathParts[3];
            const propertyPath = `/${cityName}/${communityName}/${propertyName}`;

            console.log("Checking for property in URL:", propertyPath);

            const propertyIsActive = activePolygon?.path === propertyPath;
            if (propertyIsActive) {
                console.log(
                    "Property already active, skipping URL-triggered zoom"
                );
                setShowLoader(false);
                return;
            }

            let property = allProperties.find(
                (prop) => prop.path === propertyPath
            );

            if (!property) {
                console.log(
                    "Property not found in allProperties, checking groups"
                );
                for (const group of allGroups) {
                    if (group.modal_map_coords_pin) {
                        const groupProperty = group.modal_map_coords_pin.find(
                            (prop) => prop.path === propertyPath
                        );
                        if (groupProperty) {
                            property = groupProperty;
                            console.log("Found property in group:", group.name);
                            break;
                        }
                    }
                }
            }

            if (property && Date.now() - lastZoomTimestampRef.current > 2000) {
                console.log("Found property, zooming to:", property.name);
                lastZoomTimestampRef.current = Date.now();

                // Keep loader visible during zoom transition
                setShowLoader(true);

                setTimeout(() => {
                    zoomToPolygonRef.current(property);

                    // Hide loader after a slight delay to allow transition to complete
                    setTimeout(() => {
                        setShowLoader(false);
                    }, 800);
                }, 100);
            } else if (property) {
                console.log(
                    "Skipping automatic zoom - too soon since last zoom",
                    Date.now() - lastZoomTimestampRef.current
                );
                setShowLoader(false);
            } else {
                console.error("Property not found for path:", propertyPath);
                console.log(
                    "Available properties:",
                    allProperties.map((p) => ({ name: p.name, path: p.path }))
                );
                setShowLoader(false);
            }
        } else {
            // Not a property page, hide loader if image is loaded
            if (imageLoaded) {
                setShowLoader(false);
            }
        }
    }, [
        imageLoaded,
        window.location.pathname,
        JSON.stringify(allProperties.map((p) => p.path)),
        JSON.stringify(allGroups.map((g) => g.id)),
        community?.path,
        cityName,
        communityName,
        activePolygon,
        isManualInteraction,
        zoomToPolygonRef.current ? true : false,
    ]);

    // Define the zoom to polygon function and store it in the ref
    useEffect(() => {
        const handleZoomToProperty = (item) => {
            // Handle both field naming conventions
            const birdCoordinates = item?.bird_cords || item?.bird_coords;

            if (!item || !birdCoordinates) {
                console.warn(
                    "Cannot zoom to property: missing bird coordinates for item:",
                    item?.name || "Unknown item"
                );
                return;
            }

            console.log(
                "zoomToPolygon called for:",
                item.name,
                "Active polygon before:",
                activePolygon?.name,
                "showModal:",
                showModal
            );
            setTransitionType("click");
            const { polygonSize, polygonCenter } =
                calculatePolygon(birdCoordinates);
            const container = mapRef.current;
            const imageEl = mapBackgroundRef.current;
            if (!container || !imageEl) return;

            const newZoom = item.target_zoom || 5;
            let offsetFromCenter = 0;

            if (isMobile || isVerySmallDevice) {
                offsetFromCenter = 0;
            } else {
                let baseOffset = item.offset_from_center || -20;
                if (item.modalAlign === "right") {
                    baseOffset *= -1;
                }
                offsetFromCenter = baseOffset;
            }

            const { width: containerWidth, height: containerHeight } =
                container.getBoundingClientRect();
            const ratio = naturalSize.width / naturalSize.height;
            const containerRatio = containerWidth / containerHeight;

            let defaultScaledWidth, defaultScaledHeight;
            if (ratio < containerRatio) {
                defaultScaledWidth = containerWidth * defaultZoom;
                defaultScaledHeight =
                    ((containerWidth * 1) / ratio) * defaultZoom;
            } else {
                defaultScaledWidth = containerHeight * ratio * defaultZoom;
                defaultScaledHeight = containerHeight * defaultZoom;
            }

            let newScaledWidth, newScaledHeight;
            if (ratio < containerRatio) {
                newScaledWidth = containerWidth * newZoom;
                newScaledHeight = ((containerWidth * 1) / ratio) * newZoom;
            } else {
                newScaledWidth = containerHeight * ratio * newZoom;
                newScaledHeight = containerHeight * newZoom;
            }

            if (activePolygon === item) {
                console.log("Same polygon clicked, zooming out");
                setActivePolygon(null);
                setShowModal(false);
                setModal(false);
                setZoom(defaultZoom);

                const communityUrl = `/${cityName}/${communityName}`;
                console.log("Zooming out, changing URL to:", communityUrl);
                if (window.location.pathname !== communityUrl) {
                    window.history.pushState({}, "", communityUrl);
                }

                requestAnimationFrame(() => {
                    let newOffset;
                    if (defaultPosition) {
                        newOffset = {
                            x:
                                containerWidth / 2 -
                                (defaultPosition.x / naturalSize.width) *
                                    defaultScaledWidth,
                            y:
                                containerHeight / 2 -
                                (defaultPosition.y / naturalSize.height) *
                                    defaultScaledHeight,
                        };
                    } else {
                        newOffset = {
                            x: (containerWidth - defaultScaledWidth) / 2,
                            y: (containerHeight - defaultScaledHeight) / 2,
                        };
                    }
                    const clampedOffset = clampOffset(newOffset, defaultZoom);
                    setOffset(clampedOffset);
                });
            } else {
                console.log(
                    "Different polygon clicked, zooming in to:",
                    item.name
                );
                setZoom(newZoom);
                setActivePolygon(item);
                setShowModal(true);
                setModal(true);

                if (item.path) {
                    const pathParts = item.path.split("/");
                    const propertyName = pathParts[pathParts.length - 1];
                    const propertyUrl = `/${cityName}/${communityName}/${propertyName}`;
                    console.log("Zooming in, changing URL to:", propertyUrl);
                    if (window.location.pathname !== propertyUrl) {
                        window.history.pushState({}, "", propertyUrl);
                    }
                }

                requestAnimationFrame(() => {
                    const baseOffset = {
                        x:
                            containerWidth / 2 -
                            (polygonCenter[0] / naturalSize.width) *
                                newScaledWidth,
                        y:
                            containerHeight / 2 -
                            (polygonCenter[1] / naturalSize.height) *
                                newScaledHeight,
                    };

                    const offsetAdjustmentX =
                        (containerWidth * offsetFromCenter) / 100;

                    const desiredOffset = {
                        x: baseOffset.x - offsetAdjustmentX,
                        y: baseOffset.y,
                    };

                    const clampedOffset = clampOffset(desiredOffset, newZoom);
                    setOffset(clampedOffset);
                });
            }
        };

        zoomToPolygonRef.current = handleZoomToProperty;
    }, [
        activePolygon,
        defaultPosition,
        defaultZoom,
        isMobile,
        isVerySmallDevice,
        location.pathname,
        naturalSize.height,
        naturalSize.width,
        setModal,
        setActivePolygon,
        setShowModal,
        setZoom,
        setOffset,
        clampOffset,
        calculatePolygon,
        setTransitionType,
        mapRef,
        mapBackgroundRef,
        cityName,
        communityName,
    ]);

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setIsMobile(width <= 768);
            setIsVerySmallDevice(width <= 480);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const currentPath = window.location.pathname;
            const pathParts = currentPath.split("/");
            console.log("PopState event triggered, checking URL:", currentPath);

            if (isManualInteraction) {
                console.log(
                    "PopState: Ignoring due to recent manual interaction"
                );
                return;
            }

            if (
                pathParts.length <= 3 ||
                (pathParts.length > 3 && !pathParts[3])
            ) {
                console.log("Popstate: Community URL detected, resetting view");
                setActivePolygon(null);
                setShowModal(false);
                setModal(false);
                setZoom(defaultZoom);
                centerMap(defaultZoom);
            } else if (
                pathParts.length > 3 &&
                pathParts[3] &&
                zoomToPolygonRef.current
            ) {
                const propertyPath = `/${cityName}/${communityName}/${pathParts[3]}`;
                const propertyIsActive = activePolygon?.path === propertyPath;
                if (propertyIsActive) {
                    console.log("PopState: Property already active, skipping");
                    return;
                }

                const propertyName = pathParts[3];
                let property = allProperties.find(
                    (prop) => prop.path === propertyPath
                );

                if (!property) {
                    for (const group of allGroups) {
                        if (group.modal_map_coords_pin) {
                            const groupProperty =
                                group.modal_map_coords_pin.find(
                                    (prop) => prop.path === propertyPath
                                );
                            if (groupProperty) {
                                property = groupProperty;
                                break;
                            }
                        }
                    }
                }

                const now = Date.now();
                if (property && now - lastZoomTimestampRef.current > 2000) {
                    console.log(
                        "PopState: Found property, zooming to:",
                        property.name
                    );
                    lastZoomTimestampRef.current = now;
                    setShowLoader(true);
                    setTimeout(() => {
                        zoomToPolygonRef.current(property);
                        setTimeout(() => {
                            setShowLoader(false);
                        }, 800);
                    }, 100);
                } else if (property) {
                    console.log(
                        "PopState: Skipping automatic zoom - too soon since last zoom"
                    );
                }
            }
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [
        defaultZoom,
        setModal,
        cityName,
        communityName,
        allProperties,
        allGroups,
        zoomToPolygonRef,
        centerMap,
        lastZoomTimestampRef,
        isManualInteraction,
    ]);

    // This prevents recentering during the initial property URL load
    useEffect(() => {
        // Skip centering during initial property URL load or when a property is active
        if (zoom === defaultZoom && !activePolygon && !isPropertyPage) {
            centerMap();
        }
    }, [zoom, defaultZoom, activePolygon, isPropertyPage]);

    useEffect(() => {
        const mapContainer = mapRef.current;
        if (!mapContainer) return;

        const handleWheel = (e) => {
            e.preventDefault();
            setTransitionType("wheel");
            const zoomFactor = 1.1;
            const newZoom =
                e.deltaY > 0
                    ? Math.max(minZoom, zoom / zoomFactor)
                    : Math.min(maxZoom, zoom * zoomFactor);

            const rect = mapContainer.getBoundingClientRect();
            const pointerX = e.clientX - rect.left;
            const pointerY = e.clientY - rect.top;
            const relativeX = (pointerX - offset.x) / zoom;
            const relativeY = (pointerY - offset.y) / zoom;

            const newOffsetX = pointerX - relativeX * newZoom;
            const newOffsetY = pointerY - relativeY * newZoom;

            setZoom(newZoom);
            setOffset(clampOffset({ x: newOffsetX, y: newOffsetY }, newZoom));
        };

        // mapContainer.addEventListener("wheel", handleWheel, { passive: false });
        return () => mapContainer.removeEventListener("wheel", handleWheel);
    }, [zoom, offset, minZoom, maxZoom]);

    const initialPinchDistanceRef = useRef(null);
    const initialZoomRef = useRef(zoom);
    const initialOffsetRef = useRef(offset);

    useEffect(() => {
        const mapContainer = mapRef.current;
        if (!mapContainer) return;

        const getDistance = (touch1, touch2) => {
            const dx = touch2.clientX - touch1.clientX;
            const dy = touch2.clientY - touch1.clientY;
            return Math.sqrt(dx * dx + dy * dy);
        };

        const getMidpoint = (touch1, touch2) => ({
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2,
        });

        const handleTouchStart = (e) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                initialPinchDistanceRef.current = getDistance(
                    e.touches[0],
                    e.touches[1]
                );
                initialZoomRef.current = zoom;
                initialOffsetRef.current = offset;
            }
        };

        const handleTouchMove = (e) => {
            if (e.touches.length === 2 && initialPinchDistanceRef.current) {
                e.preventDefault();
                setTransitionType("wheel");
                const newDistance = getDistance(e.touches[0], e.touches[1]);
                const newZoom = Math.max(
                    minZoom,
                    Math.min(
                        maxZoom,
                        initialZoomRef.current *
                            (newDistance / initialPinchDistanceRef.current)
                    )
                );

                const mid = getMidpoint(e.touches[0], e.touches[1]);
                const relativeX =
                    (mid.x - initialOffsetRef.current.x) /
                    initialZoomRef.current;
                const relativeY =
                    (mid.y - initialOffsetRef.current.y) /
                    initialZoomRef.current;

                const newOffsetX = mid.x - relativeX * newZoom;
                const newOffsetY = mid.y - relativeY * newZoom;

                // setZoom(newZoom);
                // setOffset(
                //     clampOffset({ x: newOffsetX, y: newOffsetY }, newZoom)
                // );
            }
        };

        const handleTouchEnd = (e) => {
            if (e.touches.length < 2) initialPinchDistanceRef.current = null;
        };

        mapContainer.addEventListener("touchstart", handleTouchStart, {
            passive: false,
        });
        mapContainer.addEventListener("touchmove", handleTouchMove, {
            passive: false,
        });
        mapContainer.addEventListener("touchend", handleTouchEnd);
        mapContainer.addEventListener("touchcancel", handleTouchEnd);

        return () => {
            mapContainer.removeEventListener("touchstart", handleTouchStart);
            mapContainer.removeEventListener("touchmove", handleTouchMove);
            mapContainer.removeEventListener("touchend", handleTouchEnd);
            mapContainer.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, [zoom, offset, minZoom, maxZoom]);

    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
        dragStart.current = { x: e.clientX, y: e.clientY };
        offsetStart.current = { ...offset };
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        const newOffset = {
            x: offsetStart.current.x + dx,
            y: offsetStart.current.y + dy,
        };
        setOffset(clampOffset(newOffset));
    };

    const handleMouseUp = () => setIsDragging(false);

    const handleTouchStart = (e) => {
        if (e.touches.length === 1) {
            const touch = e.touches[0];
            setIsDragging(true);
            dragStart.current = { x: touch.clientX, y: touch.clientY };
            offsetStart.current = { ...offset };
        }
    };

    const handleTouchMove = (e) => {
        if (!isDragging || e.touches.length !== 1) return;
        const touch = e.touches[0];
        const dx = touch.clientX - dragStart.current.x;
        const dy = touch.clientY - dragStart.current.y;
        const newOffset = {
            x: offsetStart.current.x + dx,
            y: offsetStart.current.y + dy,
        };
        setOffset(clampOffset(newOffset));
    };

    const handleTouchEnd = () => setIsDragging(false);

    const zoomStyles = {
        transform: `matrix(${zoom}, 0, 0, ${zoom}, ${offset.x}, ${offset.y})`,
        transformOrigin: "0px 0px 0px",
        // transition: isDragging
        //     ? "none"
        //     : transitionType === "wheel"
        //         ? "transform 0.3s ease-out"
        //         : "transform 0.8s ease-in-out",
    };

    // Add a separate handler for modal close button
    const handleModalClose = () => {
        console.log("Modal close button clicked");

        if (isZooming) {
            console.log("Zoom transition in progress, ignoring close request");
            return;
        }

        setIsManualInteraction(true);
        setShowLoader(true); // Show loader during transition
        setShowModal(false);
        setModal(false);

        // Update URL but keep activePolygon set on mobile
        const communityUrl = `/${cityName}/${communityName}`;
        if (window.location.pathname !== communityUrl) {
            window.history.pushState({}, "", communityUrl);
        }

        if (!isMobile) {
            // On desktop, behave the same as back button
            handleCloseModal(true);
        } else {
            // On mobile, hide loader after a brief delay
            setTimeout(() => {
                setShowLoader(false);
            }, 300);
        }
    };

    const handleCloseModal = (fromBackButton = false) => {
        console.log("Closing modal, current URL:", window.location.pathname);

        if (isZooming) {
            console.log(
                "Zoom transition in progress, ignoring close modal request"
            );
            return;
        }

        setIsManualInteraction(true); // Mark as manual interaction
        setIsZooming(true);
        setShowLoader(true); // Show loader during transition

        // Always reset these states
        setShowModal(false);
        setModal(false);
        setActivePolygon(null);
        setZoom(defaultZoom);

        requestAnimationFrame(() => {
            const container = mapRef.current;
            const imageEl = mapBackgroundRef.current;
            if (!container || !imageEl) {
                setIsZooming(false);
                setShowLoader(false);
                return;
            }

            const { width: containerWidth, height: containerHeight } =
                container.getBoundingClientRect();
            const ratio = naturalSize.width / naturalSize.height;
            const containerRatio = containerWidth / containerHeight;

            let newOffset;
            if (defaultPosition) {
                let defaultScaledWidth, defaultScaledHeight;
                if (ratio < containerRatio) {
                    defaultScaledWidth = containerWidth * defaultZoom;
                    defaultScaledHeight =
                        ((containerWidth * 1) / ratio) * defaultZoom;
                } else {
                    defaultScaledWidth = containerHeight * ratio * defaultZoom;
                    defaultScaledHeight = containerHeight * defaultZoom;
                }

                newOffset = {
                    x:
                        containerWidth / 2 -
                        (defaultPosition.x / naturalSize.width) *
                            defaultScaledWidth,
                    y:
                        containerHeight / 2 -
                        (defaultPosition.y / naturalSize.height) *
                            defaultScaledHeight,
                };
            } else {
                let defaultScaledWidth, defaultScaledHeight;
                if (ratio < containerRatio) {
                    defaultScaledWidth = containerWidth * defaultZoom;
                    defaultScaledHeight =
                        ((containerWidth * 1) / ratio) * defaultZoom;
                } else {
                    defaultScaledWidth = containerHeight * ratio * defaultZoom;
                    defaultScaledHeight = containerHeight * defaultZoom;
                }

                newOffset = {
                    x: (containerWidth - defaultScaledWidth) / 2,
                    y: (containerHeight - defaultScaledHeight) / 2,
                };
            }

            const clampedOffset = clampOffset(newOffset, defaultZoom);
            setOffset(clampedOffset);
        });

        const communityUrl = `/${cityName}/${communityName}`;
        console.log("Closing modal, changing URL to:", communityUrl);
        if (window.location.pathname !== communityUrl) {
            window.history.pushState({}, "", communityUrl);
        }

        setTimeout(() => {
            setIsZooming(false);
            setShowLoader(false);
            console.log("Close modal transition completed");
        }, 800);
    };

    // Create a debounced version of the zoom function
    useEffect(() => {
        if (!zoomToPolygonRef.current) return;

        const originalZoomFn = zoomToPolygonRef.current;

        zoomToPolygonRef.current = (item) => {
            if (isZooming) {
                console.log(
                    "Zoom already in progress, ignoring call for:",
                    item.name
                );
                return;
            }

            console.log("Starting zoom transition for:", item.name);
            setIsZooming(true);

            originalZoomFn(item);

            setTimeout(() => {
                setIsZooming(false);
                setShowLoader(false);
                console.log("Zoom transition completed for:", item.name);
            }, 800);
        };

        return () => {
            zoomToPolygonRef.current = originalZoomFn;
        };
    }, [isZooming]);

    const zoomToPolygon = (item) => {
        // Handle both field naming conventions
        const birdCoordinates = item?.bird_cords || item?.bird_coords;

        if (!item || !birdCoordinates) {
            console.warn(
                "Cannot zoom to polygon: missing bird coordinates for item:",
                item?.name || "Unknown item"
            );
            return;
        }

        console.log("zoomToPolygon wrapper called for", item.name);
        setIsManualInteraction(true); // Mark as manual interaction
        if (zoomToPolygonRef.current) {
            zoomToPolygonRef.current(item);
        }
    };

    const filteredMarkers = facilityMarkers.map(({ id, markers }) => ({
        id,
        markers: Array.isArray(markers)
            ? markers
                  .filter(
                      (marker) =>
                          marker &&
                          Array.isArray(marker.community_paths) &&
                          marker.community_paths.includes(community?.path)
                  )
                  .map((marker) => ({
                      ...marker,
                      correctCoords: Array.isArray(marker.community_paths)
                          ? marker.community_paths
                                .map((comm, index) =>
                                    comm === community?.path &&
                                    Array.isArray(marker.community_cords) &&
                                    marker.community_cords[index]
                                        ? marker.community_cords[index]
                                        : null
                                )
                                .filter((coord) => coord !== null)
                          : [],
                  }))
            : [],
    }));

    const communityProperties = allProperties.filter(
        (property) =>
            property.path?.startsWith(`/${cityName}/${communityName}/`) &&
            (property.enabled === undefined || property.enabled === true)
    );

    const communityGroupMarkersProperties = allGroups
        .map((element) => {
            if (!element || !Array.isArray(element.modal_map_coords_pin)) {
                return [];
            }
            return element.modal_map_coords_pin.filter(
                (property) =>
                    property &&
                    property.path &&
                    property.path.startsWith(
                        `/${cityName}/${communityName}/`
                    ) &&
                    (property.enabled === undefined ||
                        property.enabled === true)
            );
        })
        .flat();

    const communityMarkers = [
        ...communityProperties,
        ...communityGroupMarkersProperties,
    ];

    const getCommunityBird = () => community?.bird || "";

    const getComBirdMin = () => {
        const communityBirdMin = getCommunityBird();
        return communityBirdMin
            ? communityBirdMin
                  .replace(
                      "/communities-birds/",
                      "/communities-birds/compressed/"
                  )
                  .replace(".jpg", "-min.jpg")
            : null;
    };

    return (
        <div
            className="community-page-wrapper"
            ref={mapRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <ProgressiveImage
                ref={mapBackgroundRef}
                lowResSrc={getComBirdMin()}
                highResSrc={getCommunityBird()}
                style={zoomStyles}
                alt={"Community Map"}
                onLoad={handleEnhancedImageLoad}
            />
            {activePolygon ? (
                <div
                    className="back-btn"
                    onClick={(e) => {
                        console.log("Back button clicked");
                        e.stopPropagation();
                        handleCloseModal(true);
                    }}
                >
                    <span>
                        <img
                            src={
                                require("../../../static/img/Arrow 1.svg")
                                    .default
                            }
                            alt="Back arrow"
                        />
                        back
                    </span>
                </div>
            ) : (
                <Back link={`/${cityName}`} />
            )}
            <Compass />

            <div className="svg-wrapper" style={zoomStyles}>
                <svg viewBox="0 0 8000 4500">
                    {communityMarkers.map((item, index) => {
                        // Handle both field naming conventions (bird_cords and bird_coords)
                        const birdCoordinates =
                            item?.bird_cords || item?.bird_coords;

                        // Skip items without coordinates
                        if (!item || !birdCoordinates) {
                            console.warn(
                                "Missing bird coordinates for item:",
                                item?.name || "Unknown item"
                            );
                            return null;
                        }

                        const { polygonSize, polygonCenter } =
                            calculatePolygon(birdCoordinates);
                        const polygonBottomCenter = [
                            polygonCenter[0],
                            polygonCenter[1] + polygonSize.height / 2,
                        ];

                        return (
                            <g
                                key={index}
                                className={`marker-group ${
                                    activePolygon === item ? "active" : ""
                                }`}
                                onClick={(e) => {
                                    console.log("Polygon clicked:", item.name);
                                    e.stopPropagation();
                                    zoomToPolygon(item);
                                }}
                            >
                                <polygon
                                    points={birdCoordinates
                                        .map((coord) => coord.join(","))
                                        .join(" ")}
                                    strokeWidth={2}
                                    className="polygon"
                                />
                                <g
                                    style={{
                                        transform: `scale(${
                                            zoom < 1.5 ? 1 / zoom : 1 / 1.5
                                        })`,
                                        transformOrigin: `${polygonBottomCenter[0]}px ${polygonBottomCenter[1]}px`,
                                        // transition: isDragging
                                        //     ? "none"
                                        //     : "transform 0.3s ease-out",
                                    }}
                                >
                                    <MarkerText
                                        name={item.name}
                                        coords={polygonBottomCenter}
                                        align={item.community_align}
                                        pinSize={polygonSize}
                                        marginToNextLine={
                                            PIN_OFFSETS.default.marginToNextLine
                                        }
                                        offsets={PIN_OFFSETS.default}
                                        zoom={zoom}
                                        zoomThreshold={
                                            item.text_zoom_threshold || 1.5
                                        }
                                    />
                                </g>
                            </g>
                        );
                    })}

                    {filteredMarkers.map(({ id, markers }) =>
                        markers.map((marker, index) =>
                            marker.correctCoords.map((coords, coordIndex) => {
                                const bottomCenterCoords = [
                                    coords[0],
                                    coords[1] + PIN_SIZE.height / 2,
                                ];

                                return (
                                    <g
                                        key={`${id}-${index}-${coordIndex}`}
                                        className="marker-group"
                                        style={{
                                            transform: `scale(${1 / zoom})`,
                                            transformOrigin: `${coords[0]}px ${coords[1]}px`,
                                            transition: isDragging
                                                ? "none"
                                                : "transform 0.3s ease-out",
                                        }}
                                    >
                                        <g className="marker-icon">
                                            <svg
                                                x={
                                                    coords[0] -
                                                    PIN_SIZE.width / 2
                                                }
                                                y={
                                                    coords[1] -
                                                    PIN_SIZE.height / 2 +
                                                    15
                                                }
                                                className="image-pins"
                                                width={PIN_SIZE.width}
                                                height={PIN_SIZE.height}
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g filter="url(#filter0_d)">
                                                    <rect
                                                        x="4.84326"
                                                        y="5.65723"
                                                        width="8"
                                                        height="8"
                                                        transform="rotate(-45 4.84326 5.65723)"
                                                        fill="white"
                                                    />
                                                </g>
                                                <defs>
                                                    <filter
                                                        id="filter0_d"
                                                        x="0.843262"
                                                        y="0"
                                                        width="19.3137"
                                                        height="19.3145"
                                                        filterUnits="userSpaceOnUse"
                                                        colorInterpolationFilters="sRGB"
                                                    >
                                                        <feFlood
                                                            floodOpacity="0"
                                                            result="BackgroundImageFix"
                                                        />
                                                        <feColorMatrix
                                                            in="SourceAlpha"
                                                            type="matrix"
                                                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                                            result="hardAlpha"
                                                        />
                                                        <feOffset dy="4" />
                                                        <feGaussianBlur stdDeviation="2" />
                                                        <feComposite
                                                            in2="hardAlpha"
                                                            operator="out"
                                                        />
                                                        <feColorMatrix
                                                            type="matrix"
                                                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.35 0"
                                                        />
                                                        <feBlend
                                                            mode="normal"
                                                            in2="BackgroundImageFix"
                                                            result="effect1_dropShadow"
                                                        />
                                                        <feBlend
                                                            mode="normal"
                                                            in="SourceGraphic"
                                                            in2="effect1_dropShadow"
                                                            result="shape"
                                                        />
                                                    </filter>
                                                </defs>
                                            </svg>
                                            <MarkerText
                                                name={marker.name}
                                                coords={bottomCenterCoords}
                                                align={marker.align}
                                                pinSize={PIN_SIZE}
                                                marginToNextLine={
                                                    PIN_OFFSETS.default
                                                        .marginToNextLine
                                                }
                                                offsets={PIN_OFFSETS.default}
                                                zoom={zoom}
                                                zoomThreshold={
                                                    marker.text_zoom_threshold ||
                                                    1.5
                                                }
                                            />
                                        </g>
                                    </g>
                                );
                            })
                        )
                    )}
                </svg>
            </div>
            <AnimatePresence>
                {showModal && activePolygon && (
                    <Modal
                        property={activePolygon}
                        onClose={handleModalClose}
                    />
                )}
            </AnimatePresence>

            <Loader isVisible={showLoader} nonBlocking={true} />
        </div>
    );
}
